import { useMemo } from "react";
import type { ReactNode } from "react";

import { CreatableSelect } from "ui/creatable-select";
import type { OptionType, SelectSize } from "ui/select";
import { useCreateFolder, useFolders } from "hooks/api/use-folders";
import toast from "components/partials/toast/toast";
import { getErrorMessage } from "utilities";

interface CampaignFolderSelectProps {
  selectName: string;
  label?: ReactNode;
  ariaLabel?: string;
  placeholder?: string;
  size?: SelectSize;
  isRequired?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  value?: string | null;
  onChange: (folderId: string) => void;
  clientId?: string;
  campaignId?: string;
  enabled?: boolean;
}

const DEFAULT_PLACEHOLDER = "Create or select folder name...";

const FOLDER_LABEL = "Folder name";

const CampaignFolderSelect = ({
  selectName,
  label = FOLDER_LABEL,
  ariaLabel = "Choose folder",
  placeholder = DEFAULT_PLACEHOLDER,
  size = "medium",
  isRequired = false,
  isDisabled = false,
  isClearable,
  value,
  onChange,
  clientId,
  campaignId,
  enabled = true,
}: CampaignFolderSelectProps) => {
  // If field is required, it should not be clearable by default (unless explicitly enabled).
  const resolvedIsClearable = isClearable ?? !isRequired;
  const canQuery = enabled && !!clientId && !!campaignId;
  const { data: foldersResponse, isLoading: isFoldersLoading } = useFolders(
    { unpaged: true, sort: ["name,asc"], clientId, campaignId },
    { enabled: canQuery },
  );
  const createFolderMutation = useCreateFolder({ clientId, campaignId });

  const folderOptions = useMemo<OptionType[]>(() => {
    const folders = foldersResponse?.content ?? [];
    return folders
      .filter((f) => f.id)
      .map((folder) => ({
        label: folder.name,
        value: folder.id,
      }));
  }, [foldersResponse]);

  const selectedOption = useMemo(
    () => folderOptions.find((option) => option.value === (value ?? "")) ?? null,
    [folderOptions, value],
  );

  const handleCreateFolder = (name: string) => {
    if (!name.trim() || !clientId || !campaignId) return;

    createFolderMutation.mutate(
      { name: name.trim() },
      {
        onSuccess: (folder) => {
          if (folder?.id) {
            onChange(folder.id);
          }
        },
        onError: (error) => {
          toast.error({
            data: {
              title: "Failed to create folder",
              message: getErrorMessage(error),
            },
          });
        },
      },
    );
  };

  const isLoading = isFoldersLoading || createFolderMutation.isPending;

  return (
    <CreatableSelect
      selectName={selectName}
      label={label}
      ariaLabel={ariaLabel}
      placeholder={isFoldersLoading ? "Loading..." : placeholder}
      options={folderOptions}
      disabled={isDisabled || !canQuery}
      required={isRequired}
      disableClearable={!resolvedIsClearable}
      loading={isLoading}
      size={size}
      value={selectedOption}
      onChange={(option) => {
        onChange(option?.value ? option.value.toString() : "");
      }}
      onCreateOption={handleCreateFolder}
    />
  );
};

export default CampaignFolderSelect;
